import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Undo2,
  Clock,
  CheckCircle2,
  Search,
  ArrowUpRight,
  MoreHorizontal,
  Plus,
  Settings2,
  MessageSquareWarning,
} from 'lucide-react';
import { DataTable, type Column, type TableDensity } from '@/components/common/DataTable';
import { DensityToggle } from '@/components/common/DensityToggle';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { returnsService } from '@/services/returns';
import type { ReturnOrder, ReturnStatus } from '@/types/returns';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';
import { ReturnDetailSheet } from './ReturnDetailSheet';
import { ReturnFormSheet } from './ReturnFormSheet';

type StatusFilter = 'all' | ReturnStatus;

const STATUS_LABEL: Record<ReturnStatus, string> = {
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  RECEIVED: 'Received',
  QC_PENDING: 'QC pending',
  GOOD_INVENTORY: 'Good inv',
  BAD_INVENTORY: 'Bad inv',
  CLOSED: 'Closed',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

const STATUS_TONE: Record<ReturnStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  REQUESTED: 'warning',
  APPROVED: 'warning',
  RECEIVED: 'warning',
  QC_PENDING: 'warning',
  GOOD_INVENTORY: 'success',
  BAD_INVENTORY: 'muted',
  CLOSED: 'success',
  REJECTED: 'destructive',
  CANCELLED: 'muted',
};

export function ReturnsPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<ReturnOrder | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [density, setDensity] = useState<TableDensity>('compact');
  const debounced = useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['returns', { q: debounced }],
    queryFn: () =>
      returnsService.list({
        externalReturnRef: debounced || undefined,
        pageSize: 200,
      }),
  });

  // REQUESTED returns live on /return-requests now (the reviewer
  // queue). This page is the physical-movement view — hide REQUESTED
  // here so the two surfaces don't show the same rows.
  const allRows = useMemo(
    () => (data?.rows ?? []).filter((r) => r.status !== 'REQUESTED'),
    [data],
  );

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: allRows.length,
      REQUESTED: 0,
      APPROVED: 0,
      RECEIVED: 0,
      QC_PENDING: 0,
      GOOD_INVENTORY: 0,
      BAD_INVENTORY: 0,
      CLOSED: 0,
      REJECTED: 0,
      CANCELLED: 0,
    };
    for (const r of allRows) c[r.status] += 1;
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return allRows;
    return allRows.filter((r) => r.status === filter);
  }, [allRows, filter]);

  const summary = useMemo(
    () => ({
      open: counts.REQUESTED + counts.APPROVED + counts.RECEIVED + counts.QC_PENDING,
      qcPending: counts.QC_PENDING,
      closed: counts.CLOSED + counts.GOOD_INVENTORY + counts.BAD_INVENTORY,
    }),
    [counts],
  );

  const tabs: StatusTabOption[] = [
    { value: 'all',         label: 'All',         count: counts.all },
    { value: 'REQUESTED',   label: 'Requested',   count: counts.REQUESTED },
    { value: 'APPROVED',    label: 'Approved',    count: counts.APPROVED },
    { value: 'RECEIVED',    label: 'Received',    count: counts.RECEIVED },
    { value: 'QC_PENDING',  label: 'QC pending',  count: counts.QC_PENDING },
    { value: 'CLOSED',      label: 'Closed',      count: counts.CLOSED },
    { value: 'REJECTED',    label: 'Rejected',    count: counts.REJECTED },
  ];

  const columns: Column<ReturnOrder>[] = [
    {
      key: 'no',
      header: 'Return',
      width: '170px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-mono text-[12px] font-medium text-foreground">{r.returnNumber}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">
            {r.salesOrder.orderNumber}
          </div>
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-medium text-foreground">{r.customer.name}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">{r.customer.code}</div>
        </div>
      ),
    },
    {
      key: 'lines',
      header: 'Lines',
      align: 'right',
      width: '70px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums">{r.items.length || '—'}</span>
      ),
    },
    {
      key: 'qty',
      header: 'Claimed',
      align: 'right',
      width: '90px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums text-muted-foreground">
          {fmt.number(r.items.reduce((s, l) => s + Number(l.quantity), 0))}
        </span>
      ),
    },
    {
      key: 'received',
      header: 'Received',
      align: 'right',
      width: '90px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums">
          {fmt.number(r.items.reduce((s, l) => s + Number(l.receivedQuantity), 0))}
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      cell: (r) =>
        r.reason ? (
          <Badge variant="muted" className="text-[10px]">
            {r.reason}
          </Badge>
        ) : (
          <span className="text-[12px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'created',
      header: 'Requested at',
      width: '180px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {fmt.dateTime(r.createdAt)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '130px',
      cell: (r) => (
        <Badge variant={STATUS_TONE[r.status]} className="text-[10px]">
          {STATUS_LABEL[r.status]}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '90px',
      hoverReveal: true,
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => setSelected(r)}
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Open</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSelected(r)}>Open detail</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        <ListPageHeader
          title="Returns"
          count={counts.all}
          actions={
            <>
              <Button size="sm" variant="ghost" asChild>
                <Link to="/settings/request-reasons" title="Reason catalog + auto-approval">
                  <MessageSquareWarning className="h-3.5 w-3.5" />
                  Reasons
                </Link>
              </Button>
              <Button size="sm" variant="ghost" asChild>
                <Link to="/settings/returns" title="Returns policy">
                  <Settings2 className="h-3.5 w-3.5" />
                  Policy
                </Link>
              </Button>
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-3.5 w-3.5" />
                New return
              </Button>
            </>
          }
        />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <KpiCard label="Open returns" value={fmt.number(summary.open)} tone="primary" icon={Undo2} />
          <KpiCard
            label="QC pending"
            value={fmt.number(summary.qcPending)}
            tone={summary.qcPending ? 'warning' : 'default'}
            icon={Clock}
          />
          <KpiCard label="Closed" value={fmt.number(summary.closed)} tone="success" icon={CheckCircle2} />
        </div>

        <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="External return ref…"
              className="h-8 pl-8 text-[13px]"
            />
          </div>
          <DensityToggle value={density} onChange={setDensity} />
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            onRowClick={setSelected}
            density={density}
            emptyTitle={
              filter === 'all'
                ? 'No returns yet'
                : `No ${STATUS_LABEL[filter as ReturnStatus].toLowerCase()} returns`
            }
            emptyDescription={
              filter === 'all'
                ? 'Returns are created from a sales order — when stock comes back, log it here.'
                : 'Try a different status filter.'
            }
          />
        )}

        <ReturnDetailSheet
          record={selected}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
        />

        <ReturnFormSheet open={createOpen} onOpenChange={setCreateOpen} />
      </div>
    </TooltipProvider>
  );
}
